import type { GetConversationDetailResponse } from './contracts/conversations';

type Message = GetConversationDetailResponse['messages'][number];

/**
 * Renders a single conversation message as a markdown block for debug/admin
 * surfaces (CLI, MCP `get_conversation`). Tool calls/results/errors get
 * labelled fenced-code bodies so hub builders can see arguments and
 * responses; system events and normal bubbles use their sender→receiver
 * routing description.
 */
export function formatConversationEventAsMarkdown(msg: Message): string {
  const time = new Date(msg.created_at).toLocaleString();
  const meta = (msg.metadata ?? {}) as {
    tool_name?: unknown;
    arguments?: unknown;
    result?: unknown;
    is_error?: unknown;
  };
  const toolName = typeof meta.tool_name === 'string' ? meta.tool_name : undefined;
  const isToolCall = msg.sender_type === 'agent' && msg.receiver_type === 'tool' && toolName;
  const isToolResult = msg.sender_type === 'tool' && msg.receiver_type === 'agent' && toolName;

  if (isToolCall) {
    return `**[tool_call: ${toolName}]** (${time})${fenced(meta.arguments)}\n`;
  }
  if (isToolResult) {
    const label = meta.is_error === true ? 'tool_error' : 'tool_result';
    const body = meta.result !== undefined ? meta.result : msg.message_text;
    return `**[${label}: ${toolName}]** (${time})${fenced(body)}\n`;
  }
  if (msg.sender_type === 'system') {
    const to = msg.receiver_type ?? '-';
    return `**[system → ${to}]** (${time})\n${msg.message_text || '_[No text content]_'}\n\n`;
  }
  const from = msg.sender_description || msg.sender_type;
  const to = msg.receiver_description || msg.receiver_type;
  const routing = to ? `${from} → ${to}` : from;
  return `**[${routing}]** (${time})\n${msg.message_text || '_[No text content]_'}\n\n`;
}

function fenced(value: unknown): string {
  // Every event ends with a blank-line separator (`\n\n`). When there's no
  // value to render, return `\n` so the trailing `\n` in the caller's
  // template produces the same two-newline ending as the other branches.
  if (value === undefined || value === null) return '\n';
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return `\n\`\`\`\n${text}\n\`\`\`\n`;
}
